import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  SectionCard,
  Modal,
  ModalShell,
  FormRow,
  FieldInput,
  Divider,
  Check,
  ArrowLeft,
  ArrowRight
} from "@/components/ds";

// ── Step definitions ──────────────────────────────────────────────────────────
// Follows CLAUDE.md's WIZARD DESIGN STANDARD (re-entrant, upsert, audit-logged) using
// IvrSetupWizard.tsx's StepBar shell as the reference — NOT StarterWorkflowWizard.
// Each step corresponds to one real action from the manual Microsoft-tenant setup:
//   1. register    — Velaro's Graph app already exists; customer only grants tenant consent
//   2. permissions — customer's M365 admin grants admin consent for the two Calls.* scopes
//   3. resource    — customer's M365 admin creates the resource account in Teams Admin Center
//   4. configure   — enter tenant ID / resource account object ID / phone number into Velaro
//   5. verify      — Test button fires a REAL Graph subscription-creation call, live
//   6. done

type Step =
  "register" | "permissions" | "resource" | "configure" | "verify" | "done";
const STEPS: Step[] = [
  "register",
  "permissions",
  "resource",
  "configure",
  "verify",
  "done"
];
const STEP_LABELS: Record<Step, string> = {
  register: "Consent",
  permissions: "Permissions",
  resource: "Resource Account",
  configure: "Configure",
  verify: "Verify",
  done: "Done"
};

const GRAPH_CLIENT_ID = "c77a93cd-92a2-41e1-ad4d-c7cb54d674a3"; // Velaro's existing multitenant Graph app ("Velaro Outlook Email Integration") — reused, not a new registration.

function StepBar({ current }: { current: Step }) {
  const idx = STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = s === current;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                  done
                    ? "bg-(--ds-primary) border-(--ds-primary) text-white"
                    : active
                      ? "border-(--ds-primary) text-(--ds-primary) bg-white"
                      : "border-zinc-300 text-zinc-400 bg-white"
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span
                className={`text-xs font-medium ${active ? "text-(--ds-primary)" : done ? "text-zinc-600" : "text-zinc-400"}`}
              >
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mb-4 mx-1 ${i < idx ? "bg-(--ds-primary)" : "bg-zinc-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

interface ExistingConfig {
  configured: boolean;
  tenantId?: string;
  applicationInstanceObjectId?: string;
  assignedPhoneNumber?: string;
  webhookSecretConfigured?: boolean;
  graphSubscriptionActive?: boolean;
  graphSubscriptionExpiresAt?: string | null;
}

interface Props {
  onClose(): void;
  onSaved(): void;
}

export default function TeamsPhoneSetupWizard({ onClose, onSaved }: Props) {
  const api = useApi();
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existing, setExisting] = useState<ExistingConfig | null>(null);
  // Re-entrant choice per WIZARD DESIGN STANDARD: when a config already exists, the wizard
  // must ask explicitly rather than silently forcing a path.
  const [reentryChoice, setReentryChoice] = useState<
    "edit" | "startover" | null
  >(null);

  const [step, setStep] = useState<Step>("register");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [tenantId, setTenantId] = useState("");
  const [applicationInstanceObjectId, setApplicationInstanceObjectId] =
    useState("");
  const [assignedPhoneNumber, setAssignedPhoneNumber] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  const [verifyResult, setVerifyResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.messaging.get("TeamsPhone/Config");
        const data: ExistingConfig = await res.json();
        setExisting(data);
        if (data.configured) {
          setTenantId(data.tenantId || "");
          setApplicationInstanceObjectId(
            data.applicationInstanceObjectId || ""
          );
          setAssignedPhoneNumber(data.assignedPhoneNumber || "");
        }
      } catch (err) {
        setError(
          `Failed to load existing configuration: ${err instanceof Response ? (err as Response).status : String(err)}`
        );
      } finally {
        setLoadingExisting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function nextStep() {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  }
  function prevStep() {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  }

  function validateConfigureStep(): string | null {
    if (!tenantId.trim())
      return "Enter the customer's Microsoft 365 tenant ID.";
    if (!applicationInstanceObjectId.trim())
      return "Enter the resource account's Azure AD object ID.";
    return null;
  }

  async function handleSaveConfigure() {
    const validationError = validateConfigureStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const isUpdate = existing?.configured && reentryChoice !== "startover";
      const body = {
        tenantId: tenantId.trim(),
        applicationInstanceObjectId: applicationInstanceObjectId.trim(),
        assignedPhoneNumber: assignedPhoneNumber.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined
      };
      const res = isUpdate
        ? await api.messaging.put("TeamsPhone/Config", body)
        : await api.messaging.post("TeamsPhone/Config", body);
      if (!res.ok) {
        const text = await res.text();
        setError(
          `Save failed (HTTP ${res.status}): ${text || "unknown error"}`
        );
        return;
      }
      nextStep();
    } catch (err) {
      setError(
        `Save failed: ${err instanceof Response ? (err as Response).status : String(err)}`
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleVerify() {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.messaging.post("TeamsPhone/Config/Test", {});
      const data = await res.json();
      setVerifyResult({
        success: !!data.success,
        message: data.message || (res.ok ? "OK" : `HTTP ${res.status}`)
      });
    } catch (err) {
      setVerifyResult({
        success: false,
        message: `Request failed: ${err instanceof Response ? (err as Response).status : String(err)}`
      });
    } finally {
      setVerifying(false);
    }
  }

  if (loadingExisting) {
    return (
      <Modal
        open
        onClose={onClose}
        size="xl"
        height="tall"
        aria-label="Microsoft Teams Phone Setup"
      >
        <div className="p-8 text-sm text-zinc-500">Loading...</div>
      </Modal>
    );
  }

  // Re-entrant gate, per WIZARD DESIGN STANDARD: never silently force a path.
  if (existing?.configured && reentryChoice === null) {
    return (
      <Modal open onClose={onClose} size="xl">
        <ModalShell.Header
          title="Microsoft Teams Phone Setup"
          onClose={onClose}
        />
        <ModalShell.Body>
          <SectionCard title="Teams Phone is already configured for this site">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Tenant{" "}
              <code className="bg-zinc-100 px-1 rounded">
                {existing.tenantId}
              </code>
              , resource account{" "}
              <code className="bg-zinc-100 px-1 rounded">
                {existing.applicationInstanceObjectId || "not set"}
              </code>
              . Graph subscription is{" "}
              <span
                className={
                  existing.graphSubscriptionActive
                    ? "text-green-700 font-medium"
                    : "text-amber-700 font-medium"
                }
              >
                {existing.graphSubscriptionActive ? "active" : "not active"}
              </span>
              .
            </div>
            <div className="flex gap-3">
              <PrimaryButton
                onClick={() => {
                  setReentryChoice("edit");
                  setStep("configure");
                }}
              >
                Edit existing configuration
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setReentryChoice("startover");
                  setStep("register");
                }}
              >
                Start over
              </SecondaryButton>
              <GhostButton onClick={onClose}>Cancel</GhostButton>
            </div>
          </SectionCard>
        </ModalShell.Body>
      </Modal>
    );
  }

  const isDone = step === "done";

  return (
    <Modal open onClose={onClose} size="xl" height="tall">
      <div className="px-8 pt-8 pb-0">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-lg font-semibold text-zinc-800">
            Microsoft Teams Phone Setup
          </h1>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-zinc-400 hover:text-zinc-600 text-xl leading-none"
          >
            x
          </button>
        </div>
        {!isDone && <StepBar current={step} />}
      </div>

      <div className="flex-1 overflow-y-auto px-8 pb-2">
        {error && (
          <div
            role="alert"
            className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {step === "register" && (
          <SectionCard title="Step 1 — Tenant consent to Velaro's existing Graph app">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Teams Phone reuses Velaro's existing, already-live Microsoft Graph
              app registration (
              <strong>"Velaro Outlook Email Integration"</strong>, client ID{" "}
              <code className="bg-zinc-100 px-1 rounded text-xs">
                {GRAPH_CLIENT_ID}
              </code>
              ) — no new app registration is created per customer. Velaro adds
              the two Teams-calling permission scopes to this one app (a
              one-time, Velaro-side action). The customer's own Microsoft 365
              admin then grants <strong>tenant admin consent</strong> for those
              scopes in their own tenant.
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900">
              Send the customer's M365 admin this consent URL (replace nothing —
              Velaro's app ID is fixed):
              <div className="mt-1 font-mono break-all">
                https://login.microsoftonline.com/common/adminconsent?client_id=
                {GRAPH_CLIENT_ID}
              </div>
            </div>
          </SectionCard>
        )}

        {step === "permissions" && (
          <SectionCard title="Step 2 — Confirm the Calls.* permissions were consented">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              After the customer's admin completes consent, confirm (Azure
              Portal → App registrations → search the client ID above →{" "}
              <strong>API permissions</strong>) that both application
              permissions show a green "Granted" status for the customer's
              tenant:
            </div>
            <ul className="text-sm text-zinc-700 list-disc pl-5 space-y-1 mb-2">
              <li>
                <code className="bg-zinc-100 px-1 rounded">
                  Calls.AccessMedia.All
                </code>{" "}
                — required for Velaro's bot to be a controllable participant on
                any call (hold/resume/end/transfer all depend on this)
              </li>
              <li>
                <code className="bg-zinc-100 px-1 rounded">
                  Calls.Initiate.All
                </code>{" "}
                — required for Velaro's bot to place outbound calls as the
                resource account
              </li>
            </ul>
            <div className="text-xs text-zinc-400">
              This is a verification checkpoint, not a Velaro API call — Graph
              does not expose a per-tenant permission-status read to a
              client-credentials app.
            </div>
          </SectionCard>
        )}

        {step === "resource" && (
          <SectionCard title="Step 3 — Customer creates the Teams Phone resource account">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              This step happens entirely in the customer's own Microsoft 365
              tenant — Velaro cannot automate it. In{" "}
              <strong>Teams Admin Center</strong> (admin.teams.microsoft.com):
            </div>
            <ol className="text-sm text-zinc-700 list-decimal pl-5 space-y-1">
              <li>
                Users → Manage resource accounts → + Add → create an account
                (e.g. "Velaro Phone Bot")
              </li>
              <li>
                Assign it a <strong>Teams Phone Standard</strong> (or Teams
                Phone Resource Account) license
              </li>
              <li>
                Voice → Phone numbers → assign a number to the resource account
              </li>
              <li>
                Voice → Calling policies → assign a calling policy that allows
                the number to receive/place calls
              </li>
              <li>
                Copy the resource account's <strong>Object ID</strong> from
                Entra ID → Users (needed in Step 4)
              </li>
            </ol>
            <div className="mt-5 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                Which Teams voice license does the customer need?
              </div>
              <div className="text-sm text-zinc-600 leading-relaxed space-y-2">
                <p>
                  Microsoft offers three ways to license Teams voice - the
                  resource account above needs one of them, and which one the
                  customer already has (or picks) changes what the rest of this
                  setup looks like on their end:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Calling Plan</strong> - Microsoft-billed per-minute
                    or unlimited plan bundled with the license. Simplest to set
                    up; no separate carrier/SIP account needed.
                  </li>
                  <li>
                    <strong>Direct Routing</strong> - customer brings their own
                    SIP trunk/carrier. Most flexible (keep an existing carrier
                    relationship or numbers), but requires the customer to
                    already have a Session Border Controller or SBC-as-a-service
                    provider configured - the most setup of the three.
                  </li>
                  <li>
                    <strong>Operator Connect</strong> - a Microsoft-certified
                    carrier manages the connection instead of a self-hosted SBC.
                    Billing is per-number through the carrier - a commonly
                    reported complaint is that carriers bill for the customer's
                    entire assigned number range, even when only a fraction of
                    those numbers are actually assigned to users, so confirm the
                    customer's carrier contract before assuming Operator Connect
                    is the cheaper option.
                  </li>
                </ul>
                <p>
                  Velaro's Teams Phone integration works the same way regardless
                  of which of the three the customer uses - it talks to the
                  resource account and phone number Microsoft already
                  provisioned, not to the underlying voice-routing mechanism.
                </p>
              </div>
            </div>
          </SectionCard>
        )}

        {step === "configure" && (
          <SectionCard title="Step 4 — Enter the values into Velaro">
            <FormRow
              label="Microsoft 365 tenant ID"
              hint="Customer's Entra directory (tenant) ID"
            >
              <FieldInput
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                style={{ width: 320 }}
              />
            </FormRow>
            <FormRow
              label="Resource account object ID"
              hint="From Entra ID → Users, Step 3"
            >
              <FieldInput
                value={applicationInstanceObjectId}
                onChange={(e) => setApplicationInstanceObjectId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                style={{ width: 320 }}
              />
            </FormRow>
            <FormRow
              label="Assigned phone number"
              hint="Optional — display only"
            >
              <FieldInput
                value={assignedPhoneNumber}
                onChange={(e) => setAssignedPhoneNumber(e.target.value)}
                placeholder="+14255551234"
                style={{ width: 320 }}
              />
            </FormRow>
            <FormRow
              label="Webhook secret"
              hint="Leave blank to auto-generate on create"
            >
              <FieldInput
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={
                  existing?.webhookSecretConfigured
                    ? "Already set — leave blank to keep"
                    : "auto-generated if blank"
                }
                style={{ width: 320 }}
              />
            </FormRow>
          </SectionCard>
        )}

        {step === "verify" && (
          <SectionCard title="Step 5 — Verify the connection">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              This creates (or renews) a real Microsoft Graph Change
              Notification subscription for
              <code className="bg-zinc-100 px-1 rounded mx-1">
                /communications/calls
              </code>
              using the tenant ID and Graph app above. If the customer's admin
              consent or resource account isn't fully set up yet, this will fail
              with Graph's real error — that's expected until Steps 1-3 are
              complete on the customer's side.
            </div>
            <PrimaryButton onClick={handleVerify} disabled={verifying}>
              {verifying ? "Testing…" : "Test connection"}
            </PrimaryButton>
            {verifyResult && (
              <div
                className={`mt-4 p-3 rounded-xl text-sm border ${verifyResult.success ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}
              >
                {verifyResult.message}
              </div>
            )}
          </SectionCard>
        )}

        {step === "done" && (
          <SectionCard title="Teams Phone setup saved">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Configuration saved for this site. A recurring background job
              renews the Graph subscription automatically (Graph's max lifetime
              for calls is ~60 minutes, renewed every 15 minutes) — no further
              action needed unless you re-run this wizard.
            </div>
            <PrimaryButton onClick={onSaved}>Done</PrimaryButton>
          </SectionCard>
        )}
      </div>

      {!isDone && (
        <>
          <Divider className="my-0" />
          <div className="px-8 py-6 flex items-center justify-between">
            <div>
              {step !== "register" && (
                <GhostButton onClick={prevStep}>
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                  Back
                </GhostButton>
              )}
              {step === "register" && (
                <GhostButton onClick={onClose}>Cancel</GhostButton>
              )}
            </div>
            <div className="flex items-center gap-3">
              {step === "configure" ? (
                <PrimaryButton onClick={handleSaveConfigure} disabled={saving}>
                  {saving ? "Saving…" : "Save & Continue"}
                </PrimaryButton>
              ) : step === "verify" ? (
                <PrimaryButton onClick={nextStep}>Continue</PrimaryButton>
              ) : (
                <PrimaryButton onClick={nextStep}>
                  <span className="flex items-center gap-1">
                    Next <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </PrimaryButton>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
